import { useEffect, useState } from 'react'
import type { GitHubBranchProtectionModeDTO } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@deepguide-ai/ui/radio-group'
import { Loader2 } from 'lucide-react'
import type { RepositoryItem } from './ConnectionScanCard'

export function GitHubBranchProtectionDialog({
  open,
  onOpenChange,
  repository,
  isSaving,
  onSave,
  progress,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  repository: RepositoryItem | null
  isSaving?: boolean
  progress?: string
  onSave: (
    repository: RepositoryItem,
    mode: GitHubBranchProtectionModeDTO
  ) => Promise<boolean>
}) {
  const [selectedMode, setSelectedMode] = useState<
    GitHubBranchProtectionModeDTO | ''
  >('')

  useEffect(() => {
    if (!open) return
    setSelectedMode(repository?.branchProtectionMode ?? '')
  }, [open, repository?.id, repository?.branchProtectionMode])

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSaving) onOpenChange(nextOpen)
      }}
    >
      <DialogContent className='sm:max-w-[460px]'>
        <DialogHeader>
          <DialogTitle>
            Branch protection check{progress ? ` · ${progress}` : ''}
          </DialogTitle>
          <DialogDescription>
            Select the GitHub protection source Screenata should verify. This
            does not change your GitHub rules.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-5 px-6'>
          <p className='text-muted-foreground text-sm'>
            Choose the GitHub configuration used by{' '}
            <span className='text-foreground font-medium'>
              {repository?.fullName}
            </span>
            . Screenata will query only the selected source; it will not fall
            back to the other one.
          </p>
          <RadioGroup
            value={selectedMode}
            onValueChange={(value) =>
              setSelectedMode(value as GitHubBranchProtectionModeDTO)
            }
            className='space-y-3'
          >
            <label className='flex cursor-pointer items-start gap-3 rounded-sm border p-4'>
              <RadioGroupItem value='CLASSIC' className='mt-0.5' />
              <span>
                <span className='block text-sm font-medium'>
                  Classic branch protection
                </span>
                <span className='text-muted-foreground mt-1 block text-xs'>
                  Uses the repository branch protection endpoint.
                </span>
              </span>
            </label>
            <label className='flex cursor-pointer items-start gap-3 rounded-sm border p-4'>
              <RadioGroupItem value='RULESET' className='mt-0.5' />
              <span>
                <span className='block text-sm font-medium'>
                  GitHub Rulesets
                </span>
                <span className='text-muted-foreground mt-1 block text-xs'>
                  Uses active rules that apply to the default branch.
                </span>
              </span>
            </label>
          </RadioGroup>
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={!repository || !selectedMode || isSaving}
            onClick={async () => {
              if (!repository || !selectedMode) return
              const updated = await onSave(repository, selectedMode)
              if (updated) onOpenChange(false)
            }}
          >
            {isSaving ? <Loader2 className='animate-spin' /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
